/**
 * iOS 네이티브 카카오 로그인 — Capacitor 커스텀 플러그인(NativeKakaoLoginPlugin.swift) JS 브릿지.
 *
 * WKWebView OAuth(src/lib/kakaoAuth.js의 loginWithKakao)와 별개 경로. 카카오톡 앱 전환
 * 로그인을 지원하며, accessToken만 반환한다(Native App Key/Admin Key 등은 절대 노출 안 함).
 */
import { Capacitor, registerPlugin } from '@capacitor/core'

const NativeKakaoLogin = registerPlugin('NativeKakaoLogin')

export function isNativeKakaoLoginAvailable() {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())
}

/** 네이티브 reject를 {code, message, cancelled} 형태로 정규화 */
function normalizeNativeError(err, fallbackCode = 'LOGIN_FAILED') {
  const code = err?.code || fallbackCode
  const cancelled = err?.data?.cancelled === true || code === 'CANCELLED'
  const message = err?.message || '카카오 로그인에 실패했습니다.'
  const next = new Error(message)
  next.code = code
  next.cancelled = cancelled
  return next
}

/**
 * 네이티브 카카오 로그인 실행.
 * @returns {Promise<{accessToken: string, expiresIn?: number, refreshTokenExpiresIn?: number}>}
 */
export async function loginNative() {
  if (!isNativeKakaoLoginAvailable()) {
    const err = new Error('네이티브 앱에서만 사용할 수 있습니다.')
    err.code = 'PLUGIN_NOT_REGISTERED'
    throw err
  }
  try {
    return await NativeKakaoLogin.login()
  } catch (err) {
    throw normalizeNativeError(err)
  }
}

export async function logoutNative() {
  if (!isNativeKakaoLoginAvailable()) return
  try {
    await NativeKakaoLogin.logout()
  } catch (err) {
    throw normalizeNativeError(err, 'LOGOUT_FAILED')
  }
}

/** 구현만 해두고 어떤 UI에서도 호출하지 않음 — 앱스토어 계정 삭제 요구사항 대응 후속 작업용 */
export async function unlinkNative() {
  if (!isNativeKakaoLoginAvailable()) return
  try {
    await NativeKakaoLogin.unlink()
  } catch (err) {
    throw normalizeNativeError(err, 'UNLINK_FAILED')
  }
}

export { NativeKakaoLogin }
